import { Editor } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import BulletList from "@tiptap/extension-bullet-list";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Link from "@tiptap/extension-link";
import OrderedList from "@tiptap/extension-ordered-list";
import Strike from "@tiptap/extension-strike";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import VariableSuggestion from "./variableSuggestion";
import "./wysiwyg.scss";

export default ({ content, previewFillers = {}, usableVariables = [] }) => {
  let editor;

  return {
    updatedAt: Date.now(),
    activeTab: 'write',
    previewContent: '',
    previewFillers: previewFillers,
    usableVariables: usableVariables,
    init() {
      const _this = this;

      editor = new Editor({
        element: this.$refs.element,
        extensions: [
          StarterKit.configure({
            bulletList: {
              keepMarks: true,
              keepAttributes: false,
            },
            hardBreak: false
          }),
          Underline,
          Link.configure({
            openOnClick: false,
          }),
          TextAlign.configure({
            types: ["heading", "paragraph"],
          }),
          HorizontalRule,
          BulletList,
          OrderedList,
          Blockquote,
          Strike,
          VariableSuggestion.configure({ variables: this.usableVariables }),
        ],
        editorProps: {
          attributes: {
            class:
              "format lg:format-lg dark:format-invert focus:outline-none format-blue max-w-none",
          },
        },
        content: content,
        onCreate({ editor }) {
          _this.updatedAt = Date.now();
        },
        onUpdate({ editor }) {
          _this.updatedAt = Date.now();
        },
        onSelectionUpdate({ editor }) {
          _this.updatedAt = Date.now();
        },
      });
    },
    isLoaded() {
      return editor;
    },
    isActive(type, opts = {}) {
      return editor.isActive(type, opts);
    },
    toggleHeading(opts) {
      editor.chain().toggleHeading(opts).focus().run();
    },
    toggleBold() {
      editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      editor.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      editor.chain().focus().toggleUnderline().run();
    },
    toggleStrike() {
      editor.chain().focus().toggleStrike().run();
    },
    toggleLink() {
      const url = window.prompt("Enter the URL");
      if (url) {
        editor.chain().focus().toggleLink({ href: url }).run();
      }
    },
    removeLink() {
      editor.chain().focus().unsetLink().run();
    },
    setTextAlign(align) {
      editor.chain().focus().setTextAlign(align).run();
    },
    toggleBulletList() {
      editor.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      editor.chain().focus().toggleOrderedList().run();
    },
    toggleBlockquote() {
      editor.chain().focus().toggleBlockquote().run();
    },
    setHorizontalRule() {
      editor.chain().focus().setHorizontalRule().run();
    },
    insertVariable(variable) {
      if (editor) {
        editor.chain().focus().insertContent(variable).run();
      }
    },
    getContent() {
      this.previewContent = editor ? editor.getHTML() : '';

      // the html returned is a string, we need to convert it to a DOM object
      const parser = new DOMParser();
      const doc = parser.parseFromString(this.previewContent, 'text/html');
      this.previewContent = doc.body;

      const childrenToReturn = []
      const children = this.previewContent.children;

      // well, we can't forEach this, we gotta use a for loop
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        // each child is a node, a html node, we can use the tagName property to get the type of the node
        if (child.tagName === 'P' && !child.textContent) {
          child.innerHTML = '<br class="ProseMirror-trailingBreak">';
          childrenToReturn.push(child);
        } else {
          childrenToReturn.push(child);
        }
      }

      // Convert back to string and replace variables with styled spans for preview
      let finalContent = childrenToReturn.map(child => child.outerHTML).join('');

      // Replace {{ variable_name }} with styled spans for preview
      finalContent = this.replaceVariablesForPreview(finalContent);

      return finalContent;
    },

    replaceVariablesForPreview(content) {
      // Replace {{ variable_name }} with styled spans
      return content.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, variableName) => {
        const previewValue = this.previewFillers[variableName] || `{{ ${variableName} }}`;
        return previewValue;
      });
    },
  };
};
